import React from "react";
import config from "../config";

// Cloudflare Turnstile, replacing Google reCAPTCHA.
//
// Loaded from a script tag rather than an npm package on purpose: this
// project's build toolchain is fragile and every dependency added to it has
// cost a day. No package, no lockfile churn.
//
// With no site key configured it renders nothing and reports success, which is
// exactly how the page behaved before -- `verified` defaulted to true and the
// submit button was never disabled. So a missing or wrong key can never block a
// signup, and never shows an error box to a visitor.
//
// NOTE: this is decorative until the backend verifies the token. Turnstile
// returns a token to onVerify; the API must POST it to
// https://challenges.cloudflare.com/turnstile/v0/siteverify with the secret
// before trusting the submission. Until then a bot can call the API directly.

const SCRIPT_ID = "cf-turnstile";
const SCRIPT_SRC =
  "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

const loadScript = () =>
  new Promise((resolve, reject) => {
    if (window.turnstile) return resolve();
    let el = document.getElementById(SCRIPT_ID);
    if (!el) {
      el = document.createElement("script");
      el.id = SCRIPT_ID;
      el.src = SCRIPT_SRC;
      el.async = true;
      el.defer = true;
      document.head.appendChild(el);
    }
    el.addEventListener("load", () => resolve());
    el.addEventListener("error", reject);
  });

const Captcha = ({ onVerify }) => {
  const ref = React.useRef(null);
  const siteKey = (config.mediaforta || {}).CAPTCHA_SITE_KEY;

  React.useEffect(() => {
    // No key configured: behave as the page did before, silently.
    if (!siteKey) {
      if (onVerify) onVerify(null);
      return undefined;
    }

    let widgetId;
    let cancelled = false;

    loadScript()
      .then(() => {
        if (cancelled || !ref.current || !window.turnstile) return;
        widgetId = window.turnstile.render(ref.current, {
          sitekey: siteKey,
          callback: (token) => onVerify && onVerify(token),
          "error-callback": () => onVerify && onVerify(null),
        });
      })
      .catch(() => {
        // Script blocked or offline: do not strand the visitor.
        if (!cancelled && onVerify) onVerify(null);
      });

    return () => {
      cancelled = true;
      if (widgetId && window.turnstile) {
        try {
          window.turnstile.remove(widgetId);
        } catch (e) {
          /* widget already gone */
        }
      }
    };
    // eslint-disable-next-line
  }, [siteKey]);

  if (!siteKey) return null;
  return <div ref={ref} />;
};

export default Captcha;
